import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import logo from '../assets/logos/Logo.svg';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    document.body.style.overflow = '';
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!username.trim() || !password) {
      setError('Please enter your username and password.');
      return;
    }

    setIsSubmitting(true);

    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Unable to sign in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen font-[Montserrat] text-[#130705]">
      <aside className="relative hidden w-[44%] flex-col justify-between overflow-hidden bg-[#1a120e] px-12 py-12 text-white lg:flex xl:w-[46%]">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(165deg, #241810 0%, #1a120e 42%, #130705 100%)',
          }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-px bg-[#8d7045]/35"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-40"
          style={{
            background:
              'linear-gradient(to top, rgba(120,94,50,0.18), transparent)',
          }}
          aria-hidden
        />

        <div className="relative z-10">
          <img
            src={logo}
            alt="VBS Invest"
            className="h-8 w-auto brightness-0 invert"
          />
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="font-[Merriweather] text-[2rem] leading-[1.3] text-white xl:text-[2.35rem]">
            Manage content with the same clarity as the brand itself.
          </h1>
          <p className="mt-5 text-[15px] leading-relaxed text-[#d8cfc0]/85">
            Sign in to oversee pages, sections, and media across the VBS Invest
            platform.
          </p>
        </div>

        <p className="relative z-10 text-[12px] text-[#a8a098]">
          © {new Date().getFullYear()} VBS Invest
        </p>
      </aside>

      <main className="flex flex-1 flex-col justify-center bg-[#faf8f4] px-6 py-12 sm:px-10 lg:bg-white">
        <div className="mx-auto w-full max-w-[400px]">
          <div className="mb-10 lg:hidden">
            <img src={logo} alt="VBS Invest" className="h-7 w-auto" />
          </div>

          <div className="border border-[#e4d9c8] bg-white px-7 py-8 shadow-[0_8px_30px_rgba(19,7,5,0.04)] sm:px-8 sm:py-9">
            <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#8d7045]">
              Welcome back
            </p>
            <h2 className="mt-2 font-[Merriweather] text-[1.65rem] text-[#130705]">
              Sign in
            </h2>
            <p className="mt-2 text-sm text-[#6b635c]">
              Enter your admin credentials to continue.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
              <div>
                <label
                  htmlFor="username"
                  className="mb-1.5 block text-[13px] font-medium text-[#130705]"
                >
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full rounded-md border border-[#d8cfc0] bg-white px-3.5 py-2.5 text-sm text-[#130705] outline-none transition placeholder:text-[#b0a89f] focus:border-[#785E32] focus:ring-1 focus:ring-[#785E32]/25"
                  placeholder="admin"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-[13px] font-medium text-[#130705]"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-md border border-[#d8cfc0] bg-white px-3.5 py-2.5 pr-10 text-sm text-[#130705] outline-none transition placeholder:text-[#b0a89f] focus:border-[#785E32] focus:ring-1 focus:ring-[#785E32]/25"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="!absolute right-2.5 top-1/2 !-translate-y-1/2 !transform !bg-transparent !p-1.5 !text-[#8a8178] !shadow-none hover:!bg-transparent hover:!text-[#130705] hover:!shadow-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <FiEyeOff className="h-4 w-4" />
                    ) : (
                      <FiEye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {error ? (
                <p
                  role="alert"
                  className="border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
                >
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-1 flex w-full items-center justify-center rounded-md !bg-[#785E32] px-4 py-3 text-sm font-semibold !text-white transition hover:!bg-[#6a522c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-[12px] text-[#9a9288]">
            Authorized administrators only
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
